import { from, of, throwError } from 'rxjs'
import { ajax as rxAjax, AjaxError } from 'rxjs/ajax'
import { catchError, map, mergeMap } from 'rxjs/operators'

import { isCsrfFailure, refreshCsrfToken } from './csrfTokenStore'
import { getDefaultHeaders } from './httpUtils'

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i

const mergeWithDefaultHeaders = (headers) => ({
  ...getDefaultHeaders(),
  ...headers,
})

const ajaxOnce = (settings, csrfRetried) => {
  // careful, we are not checking same-origin
  settings.headers = mergeWithDefaultHeaders(settings.headers)

  if (settings.url && !ABSOLUTE_URL_PATTERN.test(settings.url) && !settings.url.startsWith('//')) {
    settings.url = `${API_BASE_URL || ''}${
      settings.url.startsWith('/') ? settings.url : `/${settings.url}`
    }`
  }

  settings.withCredentials = true

  // rxjs 7 resolves any status below 400 as success; keep the 2xx-only contract the
  // epics' catchError paths were written against, so 3xx never reaches them as data.
  return rxAjax(settings).pipe(
    mergeMap((response) =>
      response.status >= 200 && response.status < 300
        ? of(response)
        : throwError(
            () => new AjaxError(`ajax error ${response.status}`, response.xhr, response.request)
          )
    ),
    catchError((error) =>
      !csrfRetried && isCsrfFailure(error.status, error.response)
        ? from(refreshCsrfToken()).pipe(
            mergeMap((token) =>
              ajaxOnce(
                { ...settings, headers: { ...settings.headers, 'X-CSRFToken': token } },
                true
              )
            )
          )
        : throwError(() => error)
    )
  )
}

export const ajax = (settings) => ajaxOnce(settings, false)

const withJson = (headers) => ({
  'Content-Type': 'application/json',
  ...headers,
})

export const post = (url, body, headers, responseType = 'json') =>
  ajax({
    url,
    body,
    headers: withJson(headers),
    method: 'POST',
    responseType,
  })

export const put = (url, body, headers) =>
  ajax({
    url,
    body,
    headers: withJson(headers),
    method: 'PUT',
  })

export const get = (url, responseType = 'json') =>
  ajax({ url, responseType, method: 'GET' }).pipe(map((x) => x.response))

export const deleteRest = (url, body = {}, headers = {}) =>
  ajax({
    url,
    body,
    headers,
    method: 'DELETE',
  })

export const postWithoutBody = (url, body, headers, responseType = 'json') =>
  ajax({
    url,
    body,
    headers,
    method: 'POST',
    responseType,
  })

// rxjs 7's ajax() serializes an untyped object body as JSON; rxjs 5 form-encoded it.
// Views that read Django's request.POST only see form-encoded bodies, so keep that wire format.
export const postForm = (url, body, headers, responseType = 'json') =>
  ajax({
    url,
    body: new URLSearchParams(body).toString(),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...headers },
    method: 'POST',
    responseType,
  })

export const putWithoutBody = (url, body, headers) =>
  ajax({
    url,
    body,
    headers,
    method: 'PUT',
  })

export const patch = (url, body, headers, responseType = 'json') =>
  ajax({
    url,
    body,
    headers: withJson(headers),
    method: 'PATCH',
    responseType,
  })
